import {
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  collection,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  writeBatch,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { onSnapshotWithRetry as onSnapshot } from '../utils/firestoreSubscribe';
import { getDb } from '../firebase';

/** Timestamp | Date | number -> epoch ms. 0 when absent/unparseable. */
const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

// ─── Legacy flat messages (backward compat) ──────────────────────────────────

export const sendMessage = async (familyId, messageData) => {
  const db = getDb();
  const ref = doc(collection(db, 'families', familyId, 'messages'));
  const data = {
    ...messageData,
    createdAt: Timestamp.now()
  };
  await setDoc(ref, data);
  return { id: ref.id, ...data };
};

export const markMessageRead = async (familyId, messageId, userId) => {
  const db = getDb();
  await updateDoc(doc(db, 'families', familyId, 'messages', messageId), {
    [`read.${userId}`]: true
  });
};

export const subscribeToMessages = (familyId, callback, messageLimit = 50) => {
  const db = getDb();
  const q = query(
    collection(db, 'families', familyId, 'messages'),
    orderBy('createdAt', 'desc'),
    limit(messageLimit)
  );
  return onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .reverse();
    callback(messages);
  }, (error) => {
    console.error('Messages listener error:', error);
  });
};

// ─── Thread-based conversations ──────────────────────────────────────────────

/**
 * Build the thread-preview / title text for a message. Falls back to an
 * attachment label when the message has no text (attachment-only messages).
 * @param {{ text?: string, attachments?: Array<{fileName?: string, fileType?: string}> }} messageData
 */
export const messagePreviewText = (messageData) => {
  const text = (messageData.text || '').trim();
  if (text) return text;
  const attachments = messageData.attachments || [];
  if (attachments.length) {
    const first = attachments[0];
    if ((first.fileType || '').startsWith('image/')) return '📷 Photo';
    return `📎 ${first.fileName || 'Attachment'}`;
  }
  return '';
};

/**
 * Create a new conversation thread.
 * @param {string} familyId
 * @param {{ createdBy: string, participantIds?: string[] }} threadData
 * @returns {{ id: string, ...threadData }}
 */
export const createThread = async (
  familyId,
  { createdBy, participantIds = [], childIds = [], linkedEntity = null, title = '' }
) => {
  const db = getDb();
  const ref = doc(collection(db, 'families', familyId, 'threads'));
  const now = Timestamp.now();
  const data = {
    title,
    createdAt: now,
    createdBy,
    participantIds,
    childIds,
    linkedEntity,
    linkedKey: linkedEntity ? linkedEntityKey(linkedEntity.type, linkedEntity.id) : null,
    lastMessageAt: now,
    lastMessageText: '',
    lastMessageSenderId: '',
    messageCount: 0,
    lastReadAt: {},
  };
  await setDoc(ref, data);
  return { id: ref.id, ...data };
};

// ─── Linked entities (expense / activity / swap) ─────────────────────────────

/**
 * Flat query key for a linked record. Threads store this alongside the
 * structured `linkedEntity` so "is there already a thread about this expense?"
 * is a single-field equality query — no composite index, nothing to deploy.
 *
 * @param {string} type - one of MESSAGE_LINK_TYPES
 * @param {string} id   - the linked document's id
 * @returns {string} e.g. "expense:abc123"
 */
export const linkedEntityKey = (type, id) => `${type}:${id}`;

/**
 * Find the existing thread for a linked record, or create one.
 *
 * "Discuss" on an expense must reopen the conversation that already exists,
 * not start a parallel one — otherwise the record fragments across threads and
 * the context is worse than no linking at all.
 *
 * @param {string} familyId
 * @param {Object} opts
 * @param {{type: string, id: string, label: string}} opts.linkedEntity
 * @param {string} opts.createdBy
 * @param {string[]} [opts.participantIds]
 * @param {string[]} [opts.childIds]
 * @param {string} [opts.title] - seed title when the thread is created
 * @returns {Promise<{id: string, created: boolean}>}
 */
export const findOrCreateLinkedThread = async (
  familyId,
  { linkedEntity, createdBy, participantIds = [], childIds = [], title = '' }
) => {
  const db = getDb();
  const key = linkedEntityKey(linkedEntity.type, linkedEntity.id);

  const existing = await getDocs(
    query(
      collection(db, 'families', familyId, 'threads'),
      where('linkedKey', '==', key),
      limit(1)
    )
  );

  if (!existing.empty) {
    const found = existing.docs[0];
    return { id: found.id, created: false, ...found.data() };
  }

  const thread = await createThread(familyId, {
    createdBy,
    participantIds,
    childIds,
    linkedEntity,
    title: title || linkedEntity.label || '',
  });
  return { ...thread, created: true };
};

/**
 * Replace a thread's child tags.
 * @param {string[]} childIds
 */
export const updateThreadChildren = async (familyId, threadId, childIds) => {
  const db = getDb();
  await updateDoc(doc(db, 'families', familyId, 'threads', threadId), { childIds });
};

/**
 * Subscribe to all threads for a family, ordered by most recent message.
 */
export const subscribeToThreads = (familyId, callback) => {
  const db = getDb();
  const q = query(
    collection(db, 'families', familyId, 'threads'),
    orderBy('lastMessageAt', 'desc')
  );
  return onSnapshot(q, (snapshot) => {
    const threads = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(threads);
  }, (error) => {
    console.error('Threads listener error:', error);
  });
};

/** Messages loaded on first open of a thread. */
export const THREAD_PAGE_SIZE = 50;

/**
 * Subscribe to a single thread document.
 *
 * The thread doc carries state the message list needs but messages don't hold
 * themselves: `lastReadAt` (which drives read receipts), child tags, and the
 * linked record. Callers that render a thread need to react to those changing.
 */
export const subscribeToThread = (familyId, threadId, callback) => {
  const db = getDb();
  return onSnapshot(
    doc(db, 'families', familyId, 'threads', threadId),
    (snapshot) => {
      callback(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null);
    },
    (error) => {
      console.error('Thread listener error:', error);
    }
  );
};

/**
 * Subscribe to messages within a specific thread, newest `messageLimit` first.
 *
 * Paging works by GROWING this limit rather than by stitching a second
 * `getDocs` query onto the end of a live listener. One listener means one
 * source of truth: reactions and pins on older messages keep updating live
 * after they're paged in, and there is no merge step to get subtly wrong.
 * The cost is re-reading the already-loaded page on each expansion, which is
 * the right trade at two-parent thread sizes.
 *
 * The callback receives `(messages, meta)`. `meta.hasMore` is true when the
 * page came back full, meaning older messages may exist — callers use it to
 * decide whether to offer "Load earlier messages". Existing callers that
 * ignore the second argument keep working unchanged.
 *
 * @param {function(Array, {hasMore: boolean}): void} callback
 * @param {number} [messageLimit]
 */
export const subscribeToThreadMessages = (
  familyId,
  threadId,
  callback,
  messageLimit = THREAD_PAGE_SIZE
) => {
  const db = getDb();
  const q = query(
    collection(db, 'families', familyId, 'threads', threadId, 'messages'),
    orderBy('createdAt', 'desc'),
    limit(messageLimit)
  );
  return onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .reverse();
    callback(messages, { hasMore: snapshot.docs.length >= messageLimit });
  }, (error) => {
    console.error('Thread messages listener error:', error);
  });
};

/**
 * Send a message within a thread. Atomically updates thread metadata.
 * If the thread has no title yet, sets it from the message text.
 */
export const sendThreadMessage = async (familyId, threadId, messageData) => {
  const db = getDb();
  const batch = writeBatch(db);
  const now = Timestamp.now();

  // Add the message
  const msgRef = doc(collection(db, 'families', familyId, 'threads', threadId, 'messages'));
  batch.set(msgRef, {
    ...messageData,
    createdAt: now,
  });

  // Update thread metadata
  const threadRef = doc(db, 'families', familyId, 'threads', threadId);
  const preview = messagePreviewText(messageData);
  const threadUpdate = {
    lastMessageAt: now,
    lastMessageText: preview.slice(0, 100),
    lastMessageSenderId: messageData.senderId,
  };

  // Set title from first message if thread has no title yet
  if (messageData._isFirstMessage) {
    threadUpdate.title = preview.slice(0, 50);
  }

  batch.update(threadRef, threadUpdate);

  await batch.commit();
  return { id: msgRef.id, ...messageData, createdAt: now };
};

/**
 * Mark a thread as read by a user (sets lastReadAt timestamp).
 */
export const markThreadRead = async (familyId, threadId, userId) => {
  const db = getDb();
  await updateDoc(doc(db, 'families', familyId, 'threads', threadId), {
    [`lastReadAt.${userId}`]: Timestamp.now(),
  });
};

/**
 * Delete a thread (parents only — enforced by Firestore rules).
 */
export const deleteThread = async (familyId, threadId) => {
  const db = getDb();
  await deleteDoc(doc(db, 'families', familyId, 'threads', threadId));
};

/**
 * Toggle a user's emoji reaction on a thread message.
 * Reactions are stored as `reactions.{emoji}: [uid, ...]`. The caller passes
 * whether the user has already reacted so we add or remove accordingly.
 * @param {boolean} hasReacted - true if userId is already in reactions[emoji]
 */
export const toggleMessageReaction = async (
  familyId,
  threadId,
  messageId,
  emoji,
  userId,
  hasReacted
) => {
  const db = getDb();
  const msgRef = doc(db, 'families', familyId, 'threads', threadId, 'messages', messageId);
  await updateDoc(msgRef, {
    [`reactions.${emoji}`]: hasReacted ? arrayRemove(userId) : arrayUnion(userId),
  });
};

// ─── Pinned messages ─────────────────────────────────────────────────────────

/**
 * Pin or unpin a message.
 *
 * `pinned`, `pinnedBy` and `pinnedAt` are on the Firestore rules' allowlist of
 * mutable message fields (see firestore.rules) — the message body itself stays
 * frozen. Unpinning writes `pinned: false` rather than deleting the keys so the
 * write touches the same key set either way.
 *
 * @param {boolean} pinned - desired state
 * @param {string} userId - who is pinning
 */
export const toggleMessagePin = async (familyId, threadId, messageId, pinned, userId) => {
  const db = getDb();
  await updateDoc(
    doc(db, 'families', familyId, 'threads', threadId, 'messages', messageId),
    {
      pinned,
      pinnedBy: pinned ? userId : '',
      pinnedAt: pinned ? Timestamp.now() : null,
    }
  );
};

/**
 * Subscribe to a thread's pinned messages.
 *
 * A dedicated query rather than a filter over the loaded page: a pin on a
 * message from six months ago has to stay reachable without paging back
 * through the whole thread, which is the entire point of pinning.
 */
export const subscribeToPinnedMessages = (familyId, threadId, callback) => {
  const db = getDb();
  // Equality filter only, sorted client-side. Adding `orderBy('createdAt')`
  // here would turn this into a composite-index query, so the feature would
  // silently return nothing in production until an index deploy caught up.
  // Pinned sets are small by nature; sorting them in memory costs nothing.
  const q = query(
    collection(db, 'families', familyId, 'threads', threadId, 'messages'),
    where('pinned', '==', true)
  );
  return onSnapshot(q, (snapshot) => {
    const pinned = snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
    callback(pinned);
  }, (error) => {
    console.error('Pinned messages listener error:', error);
  });
};

// ─── Search ──────────────────────────────────────────────────────────────────

/**
 * Search message BODIES across threads.
 *
 * Firestore has no full-text search, so this fans out one bounded query per
 * thread and filters in memory. That is viable here specifically because a
 * co-parenting family has a handful of threads, not thousands — the shape does
 * not generalize to a group-chat product, and the per-thread cap keeps a long
 * history from turning one search into a very expensive read.
 *
 * Substring matching, case-insensitive. Deliberately not token/prefix matching:
 * people search these threads for fragments they half-remember ("dentist",
 * "cleats"), and substring is the behavior that finds those.
 *
 * @param {string} familyId
 * @param {Array<{id: string, title?: string}>} threads - threads to search
 * @param {string} queryText
 * @param {Object} [opts]
 * @param {number} [opts.perThreadLimit] - newest N messages examined per thread
 * @param {number} [opts.maxResults] - cap on returned matches
 * @returns {Promise<Array>} matches, newest first, each with threadId/threadTitle
 */
export const searchMessages = async (
  familyId,
  threads,
  queryText,
  { perThreadLimit = 300, maxResults = 60 } = {}
) => {
  const needle = (queryText || '').trim().toLowerCase();
  if (!needle || !familyId || !threads?.length) return [];

  const db = getDb();

  const perThread = await Promise.all(
    threads.map(async (thread) => {
      try {
        const snap = await getDocs(
          query(
            collection(db, 'families', familyId, 'threads', thread.id, 'messages'),
            orderBy('createdAt', 'desc'),
            limit(perThreadLimit)
          )
        );
        return snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(m => (m.text || '').toLowerCase().includes(needle))
          .map(m => ({
            ...m,
            threadId: thread.id,
            threadTitle: thread.title || 'New Conversation',
          }));
      } catch (error) {
        // One unreadable thread should not fail the whole search.
        console.error(`Search failed for thread ${thread.id}:`, error);
        return [];
      }
    })
  );

  return perThread
    .flat()
    .sort((a, b) => (toMillis(b.createdAt) - toMillis(a.createdAt)))
    .slice(0, maxResults);
};

/**
 * Build a short snippet centered on the match, for search result rows.
 * @returns {{before: string, match: string, after: string}}
 */
export const buildSearchSnippet = (text, queryText, radius = 40) => {
  const source = text || '';
  const idx = source.toLowerCase().indexOf((queryText || '').trim().toLowerCase());
  if (idx === -1) return { before: source.slice(0, radius * 2), match: '', after: '' };

  const start = Math.max(0, idx - radius);
  const end = Math.min(source.length, idx + queryText.length + radius);
  return {
    before: (start > 0 ? '…' : '') + source.slice(start, idx),
    match: source.slice(idx, idx + queryText.length),
    after: source.slice(idx + queryText.length, end) + (end < source.length ? '…' : ''),
  };
};

// ─── Read receipts ───────────────────────────────────────────────────────────

/**
 * Has anyone other than `excludeUserId` read the thread as of `message`?
 *
 * Read state is tracked per thread (`lastReadAt`), not per message, so this is
 * a timestamp comparison rather than a lookup: a co-parent whose last read is
 * at or after the message's own timestamp has necessarily seen it.
 *
 * @param {Object} thread - thread doc with a lastReadAt map
 * @param {Object} message - message doc with createdAt
 * @param {string} excludeUserId - normally the sender; their own read is not a receipt
 * @returns {{seen: boolean, seenBy: string[], seenAt: number}}
 */
export const messageSeenState = (thread, message, excludeUserId) => {
  const sentAt = toMillis(message?.createdAt);
  const lastReadAt = thread?.lastReadAt || {};

  const seenBy = [];
  let latest = 0;

  for (const [uid, stamp] of Object.entries(lastReadAt)) {
    if (uid === excludeUserId) continue;
    const readAt = toMillis(stamp);
    if (sentAt && readAt >= sentAt) {
      seenBy.push(uid);
      if (readAt > latest) latest = readAt;
    }
  }

  return { seen: seenBy.length > 0, seenBy, seenAt: latest };
};

// ─── Scheduled messages ──────────────────────────────────────────────────────

/**
 * Queue a message for later delivery.
 *
 * The message does NOT exist in the thread until the delivery function writes
 * it, so a scheduled message carries the timestamp of when it actually landed.
 * That keeps the thread honest as a record: nothing is back-dated, and nothing
 * appears in the transcript before it was readable by the other parent.
 *
 * @param {Object} payload
 * @param {string} payload.threadId
 * @param {string} payload.senderId
 * @param {string} payload.senderName
 * @param {string} payload.text
 * @param {Array} [payload.attachments]
 * @param {Date} payload.scheduledFor
 * @returns {Promise<{id: string}>}
 */
export const scheduleMessage = async (familyId, payload) => {
  const db = getDb();
  const ref = doc(collection(db, 'families', familyId, 'scheduledMessages'));
  const data = {
    threadId: payload.threadId,
    senderId: payload.senderId,
    senderName: payload.senderName || '',
    text: payload.text || '',
    attachments: payload.attachments || [],
    scheduledFor: Timestamp.fromDate(payload.scheduledFor),
    status: 'pending',
    createdAt: Timestamp.now(),
  };
  await setDoc(ref, data);
  return { id: ref.id, ...data };
};

/**
 * Subscribe to a thread's still-pending scheduled messages.
 * Delivered ones drop out of the query as the function flips their status.
 */
export const subscribeToScheduledMessages = (familyId, threadId, callback) => {
  const db = getDb();
  const q = query(
    collection(db, 'families', familyId, 'scheduledMessages'),
    where('threadId', '==', threadId),
    where('status', '==', 'pending')
  );
  return onSnapshot(q, (snapshot) => {
    const pending = snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => toMillis(a.scheduledFor) - toMillis(b.scheduledFor));
    callback(pending);
  }, (error) => {
    console.error('Scheduled messages listener error:', error);
  });
};

/** Cancel a queued message before it is delivered. */
export const cancelScheduledMessage = async (familyId, scheduledId) => {
  const db = getDb();
  await deleteDoc(doc(db, 'families', familyId, 'scheduledMessages', scheduledId));
};
